import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { authClient } from "@/lib/auth-client";
import { toast } from "@/lib/toast";
import { Button } from "@/components/Ui";
import { AnimatedEmoji } from "@/components/AnimatedEmoji";

export default function VerifyOTP() {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const email = searchParams.get("email") ?? "";
	const [otp, setOtp] = useState(["", "", "", "", "", ""]);
	const [submitting, setSubmitting] = useState(false);
	const [resending, setResending] = useState(false);
	const [cooldown, setCooldown] = useState(60);
	const [verified, setVerified] = useState(false);
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	useEffect(() => {
		if (!email) {
			navigate("/register", { replace: true });
			return;
		}
		inputRefs.current[0]?.focus();
	}, [email, navigate]);

	useEffect(() => {
		if (cooldown <= 0) return;
		const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
		return () => clearTimeout(t);
	}, [cooldown]);

	const handleChange = (index: number, value: string) => {
		if (!/^\d*$/.test(value)) return;
		const next = [...otp];
		next[index] = value.slice(-1);
		setOtp(next);
		if (value && index < 5) {
			inputRefs.current[index + 1]?.focus();
		}
		if (index === 5 && value) {
			const fullCode = next.join("");
			if (fullCode.length === 6 && !submitting) {
				setSubmitting(true);
				authClient.emailOtp.verifyEmail({ email, otp: fullCode }).then(async ({ error: verifyError }) => {
					if (verifyError) {
						toast.error(verifyError.message ?? "Kode tidak valid");
						setOtp(["", "", "", "", "", ""]);
						inputRefs.current[0]?.focus();
						setSubmitting(false);
						return;
					}
					setVerified(true);
					await authClient.getSession();
					setTimeout(() => navigate("/onboarding", { replace: true }), 1500);
				}).catch(() => {
					toast.error("Terjadi kesalahan");
					setSubmitting(false);
				});
			}
		}
	};

	const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
		if (e.key === "Backspace" && !otp[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (e: React.ClipboardEvent) => {
		e.preventDefault();
		const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
		if (!text) return;
		const next = [...otp];
		for (let i = 0; i < 6; i++) next[i] = text[i] ?? "";
		setOtp(next);
		const focusIdx = text.length < 6 ? text.length : 5;
		inputRefs.current[focusIdx]?.focus();
	};

	const code = otp.join("");

	const handleVerify = async (e: React.FormEvent) => {
		e.preventDefault();
		if (code.length !== 6) {
			toast.error("Masukkan 6 digit kode");
			return;
		}
		setSubmitting(true);
		try {
			const { error: verifyError } = await authClient.emailOtp.verifyEmail({
				email,
				otp: code,
			});
			if (verifyError) {
				toast.error(verifyError.message ?? "Kode tidak valid");
				setOtp(["", "", "", "", "", ""]);
				inputRefs.current[0]?.focus();
				return;
			}
			setVerified(true);
			await authClient.getSession();
			setTimeout(() => navigate("/onboarding", { replace: true }), 1500);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Terjadi kesalahan");
		} finally {
			setSubmitting(false);
		}
	};

	const handleResend = async () => {
		if (cooldown > 0) return;
		setResending(true);
		try {
			const { error: resendError } = await authClient.emailOtp.sendVerificationOtp({
				email,
				type: "email-verification",
			});
			if (resendError) {
				toast.error(resendError.message ?? "Gagal mengirim ulang kode");
				return;
			}
			setCooldown(60);
			toast.success("Kode verifikasi dikirim ulang");
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Terjadi kesalahan");
		} finally {
			setResending(false);
		}
	};

	if (!email) return null;

	return (
		<div
			style={{
				flex: 1,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<div style={{ width: "100%", maxWidth: 360 }}>
				<div style={{ textAlign: "center", marginBottom: "1.75rem" }}>
					{!verified && <AnimatedEmoji name="warning" size={56} style={{ marginInline: "auto" }} />}
					<h1
						style={{
							fontSize: "1.5rem",
							fontWeight: 800,
							margin: !verified ? "0.75rem 0 0.35rem" : "0 0 0.35rem",
						}}
					>
						{verified ? "Terverifikasi!" : "Verifikasi Email"}
					</h1>
					<p
						style={{
							fontSize: "0.85rem",
							color: "var(--color-muted)",
							margin: 0,
							lineHeight: 1.5,
						}}
					>
						{verified
							? "Akun kamu berhasil diverifikasi. Mengalihkan..."
							: (
								<>
									Kode 6 digit telah dikirim ke{" "}
									<span style={{ fontWeight: 600, color: "var(--color-text)" }}>
										{email}
									</span>
								</>
							)}
					</p>
				</div>

				{!verified && (
					<form onSubmit={handleVerify}>
						<div
							style={{
								display: "flex",
								gap: "0.5rem",
								justifyContent: "center",
								marginBottom: "1.25rem",
							}}
						>
							{otp.map((digit, i) => (
								<input
									key={i}
									ref={(el) => { inputRefs.current[i] = el; }}
									type="text"
									inputMode="numeric"
									maxLength={1}
									value={digit}
									onChange={(e) => handleChange(i, e.target.value)}
									onKeyDown={(e) => handleKeyDown(i, e)}
									onPaste={i === 0 ? handlePaste : undefined}
									style={{
										width: "48px",
										height: "56px",
										textAlign: "center",
										fontSize: "1.4rem",
										fontWeight: 700,
										borderRadius: "var(--radius-md)",
										border: "1.5px solid var(--color-border)",
										background: "var(--color-surface)",
										color: "var(--color-text)",
										outline: "none",
										transition: "border-color 200ms ease",
									}}
									onFocus={(e) =>
										(e.currentTarget.style.borderColor = "var(--color-primary)")
									}
									onBlur={(e) =>
										(e.currentTarget.style.borderColor = "var(--color-border)")
									}
								/>
							))}
						</div>
						<Button
							type="submit"
							disabled={submitting || code.length !== 6}
							variant="primary"
							style={{ width: "100%" }}
						>
							{submitting ? "..." : "Verifikasi"}
						</Button>
					</form>
				)}

				{!verified && (
					<p
						style={{
							fontSize: "0.82rem",
							marginTop: "1.25rem",
							textAlign: "center",
							color: "var(--color-muted)",
						}}
					>
						Tidak menerima kode?{" "}
						<button
							type="button"
							onClick={handleResend}
							disabled={cooldown > 0 || resending}
							style={{
								background: "none",
								border: "none",
								color:
									cooldown > 0
										? "var(--color-muted)"
										: "var(--color-primary)",
								cursor: cooldown > 0 ? "default" : "pointer",
								fontSize: "0.82rem",
								fontWeight: 600,
								padding: 0,
							}}
						>
							{cooldown > 0
								? `Kirim ulang dalam ${cooldown}s`
								: resending
									? "Mengirim..."
									: "Kirim ulang"}
						</button>
					</p>
				)}

				<p
					style={{
						fontSize: "0.82rem",
						marginTop: "0.75rem",
						textAlign: "center",
						color: "var(--color-muted)",
					}}
				>
					<Link
						to="/register"
						style={{ color: "var(--color-primary)", fontWeight: 500 }}
					>
						Ganti email?
					</Link>
				</p>
			</div>
		</div>
	);
}
